import React, { useState, useEffect } from "react";
import { Modal, Table, Tag, Typography, message, Space, Empty } from "antd";
import { ExclamationCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";

const { Text } = Typography;

const ViewDisciplinaryRecordsModal = ({
	visible,
	onCancel,
	userId,
	userName,
}) => {
	const [records, setRecords] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (visible && userId) {
			fetchDisciplinaryRecords();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run only when visible/userId
	}, [visible, userId]);

	const fetchDisciplinaryRecords = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/users/disciplinary-records/${userId}/`
			);
			if (response.status === 200) {
				setRecords(response.data);
			}
		} catch (error) {
			message.error("징계 기록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const getDisciplinaryColor = (type) => {
		switch (type) {
			case "출교":
				return "red";
			case "파면":
				return "volcano";
			case "정직":
				return "orange";
			case "감봉":
				return "gold";
			case "견책":
				return "lime";
			case "근신":
				return "blue";
			default:
				return "default";
		}
	};

	const columns = [
		{
			title: "징계 유형",
			dataIndex: "type",
			key: "type",
			render: (type) => (
				<Tag color={getDisciplinaryColor(type)}>
					{type}
				</Tag>
			),
		},
		{
			title: "결의일",
			dataIndex: "resolution_date",
			key: "resolution_date",
			render: (date) => (
				<Space>
					<CalendarOutlined />
					{dayjs(date).format("YYYY-MM-DD")}
				</Space>
			),
		},
		{
			title: "징계 시작일",
			dataIndex: "start_date",
			key: "start_date",
			render: (date) => (
				<Space>
					<CalendarOutlined />
					{dayjs(date).format("YYYY-MM-DD")}
				</Space>
			),
		},
		{
			title: "징계 기간",
			dataIndex: "period",
			key: "period",
			render: (period) => `${period}일`,
		},
		{
			title: "징계 사유",
			dataIndex: "reason",
			key: "reason",
			render: (reason) => (
				<Text
					style={{
						maxWidth: 300,
						display: "block",
					}}>
					{reason || "-"}
				</Text>
			),
		},
		{
			title: "상태",
			key: "status",
			render: (_, record) => {
				const startDate = dayjs(record.start_date);
				const endDate = startDate.add(
					record.period,
					"day"
				);
				const today = dayjs();

				if (today.isBefore(startDate)) {
					return <Tag color="orange">예정</Tag>;
				} else if (today.isAfter(endDate)) {
					return <Tag color="green">완료</Tag>;
				} else {
					return <Tag color="red">진행중</Tag>;
				}
			},
		},
	];

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}>
					<ExclamationCircleOutlined />
					<span>{userName}님의 징계 기록</span>
				</div>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			width="80%"
			style={{ top: 20 }}>
			<div style={{ marginBottom: 16 }}>
				<Text type="secondary">
					{userName}님의 모든 징계 기록을 확인할
					수 있습니다.
				</Text>
			</div>

			<Table
				columns={columns}
				dataSource={records}
				rowKey="id"
				loading={loading}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showQuickJumper: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} / 총 ${total}개`,
				}}
				locale={{
					emptyText: (
						<Empty
							description="징계 기록이 없습니다."
							image={
								Empty.PRESENTED_IMAGE_SIMPLE
							}
						/>
					),
				}}
				scroll={{ x: 800 }}
			/>
		</Modal>
	);
};

export default ViewDisciplinaryRecordsModal;
